// =============================================================================
// MARK: CONSTANTS
// =============================================================================

// Credit card numbers typically range from 13 to 19 digits.
// See: https://en.wikipedia.org/wiki/Payment_card_number
const CREDIT_CARD_LENGTH = {
    MIN: 13,
    MAX: 19,
};

const PATTERNS = {
    DIGIT_SEQUENCES: /\d+/g,
    DIGITS_ONLY: /^\d+$/,
    SEPARATORS: /[\s-]/g,
};

// =============================================================================
// MARK: LOW-LEVEL UTILITY FUNCTIONS
// =============================================================================

/**
 * @param {string} inputText
 * @returns {string}
 */
function removeSeparators(inputText) {
    const inputWithoutSeparators = inputText.replace(PATTERNS.SEPARATORS, "");
    return inputWithoutSeparators;
}

/**
 * @param {string} inputText
 * @returns {boolean}
 */
function getIsValidCreditCardLength(inputText) {
    const isAboveMinLength = inputText.length >= CREDIT_CARD_LENGTH.MIN;
    const isBelowMaxLength = inputText.length <= CREDIT_CARD_LENGTH.MAX;

    return isAboveMinLength && isBelowMaxLength;
}

// =============================================================================
// MARK: DETECTION LOGIC
// =============================================================================

/**
 * @param {string} inputText
 * @returns {boolean}
 */
function getIsPotentialCreditCardNumber(inputText) {
    // First, check the input with separators removed
    const inputWithoutSeparators = removeSeparators(inputText);
    const isValidCreditCardLength = getIsValidCreditCardLength(
        inputWithoutSeparators,
    );
    const isAllDigits = PATTERNS.DIGITS_ONLY.test(inputWithoutSeparators);

    if (isValidCreditCardLength && isAllDigits) {
        return true;
    }

    // If that fails, check for card numbers embedded within the input
    const digitSequences = inputText.match(PATTERNS.DIGIT_SEQUENCES) || [];
    const isPotentialCreditCardNumber = digitSequences.some((sequence) =>
        getIsValidCreditCardLength(sequence),
    );

    return isPotentialCreditCardNumber;
}

// =============================================================================
// MARK: PUBLIC API
// =============================================================================

/**
 * This validation was implemented after a PCI (Payment Card Industry) audit
 * identified that credit card numbers were occasionally entered into fields not
 * intended for sensitive data. This was often due to browser autofill mistakenly
 * populating fields such as First_Name, Last_Name, Address, or Address_2 with card
 * numbers. This check helps prevent both users and autofill from submitting credit
 * card data in inappropriate fields.
 * @param {string} userInput
 * @returns {boolean} - true if the input does NOT contain a credit card number
 */
export function validateIsNotACreditCardNumber(userInput) {
    const trimmedInput = userInput?.trim() || "";
    if (!trimmedInput) {
        return true;
    }

    const isPotentialCreditCardNumber =
        getIsPotentialCreditCardNumber(trimmedInput);
    return !isPotentialCreditCardNumber;
}
